import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ChargerService } from '../../services/charger.service';
import { LocationService } from '../../../locations/services/location.service';
import { Store } from '@ngrx/store';
import { MessageTranslateService } from '../../../../shared/services/message.service';
import * as currentChargerActions from './chargerOverview.actions';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import * as selectors from './chargerOverview.selectors';
import { NotesService } from '../../services/notes.service';
import { NoteDto } from '../../../../core/cms-api/models';

export class CurrentChargerEffects {
  actions$: Actions = inject(Actions);
  chargerService: ChargerService = inject(ChargerService);
  locationService: LocationService = inject(LocationService);
  notesService: NotesService = inject(NotesService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);

  loadCharger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(currentChargerActions.loadChargerOverview),
      withLatestFrom(this.store.select(selectors.selectCurrentChargerOverview)),
      switchMap(([requestData, currentChargerOverview]) => {
        if (
          requestData.force ||
          requestData.chargerId !== currentChargerOverview?.id
        ) {
          console.log('loading new charger');
          return this.chargerService
            .getChargerOverview(requestData.chargerId)
            .pipe(
              map((res) => {
                if (res.isSuccess) {
                  return currentChargerActions.loadChargerOverviewSuccess({
                    chargerOverview: res.value!,
                  });
                } else {
                  return currentChargerActions.loadChargerOverviewFailure({
                    error: res.error!,
                  });
                }
              }),
              catchError((err) =>
                of(
                  currentChargerActions.loadChargerOverviewFailure({
                    error: err.message || 'Loading chargers failed',
                  })
                )
              )
            );
        } else {
          console.log('loading existing one');
          return of(
            currentChargerActions.loadChargerOverviewSuccess({
              chargerOverview: currentChargerOverview!,
            })
          );
        }
      })
    )
  );

  updateChargerLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(currentChargerActions.updateChargerLocation),
      switchMap((requestData) => {
        return this.locationService.updateLocation(requestData.location).pipe(
          map((_) => {
            return currentChargerActions.updateChargerLocationSuccess({
              location: requestData.location,
            });
          }),
          catchError((err) =>
            of(
              currentChargerActions.updateChargerLocationFailure({
                error: err.message || 'Update charger location failed',
              })
            )
          )
        );
      })
    )
  );

  showErrorForChargers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          currentChargerActions.loadChargerOverviewFailure,
          currentChargerActions.updateChargerLocationFailure,
          currentChargerActions.saveNewNoteFailure
        ),
        tap((err) => {
          console.log('CAUGHT ERROR:', err);
          this.messageService.error(err.error);
        })
      ),
    { dispatch: false }
  );

  showSuccessfulUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          currentChargerActions.updateChargerLocationSuccess,
          currentChargerActions.saveNewNoteSuccess
        ),
        tap((_) =>
          this.messageService.successTranslate('general.update-successful')
        )
      ),
    { dispatch: false }
  );

  saveNewNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(currentChargerActions.saveNewNote),
      withLatestFrom(this.store.select(selectors.selectCurrentChargerOverview)),
      switchMap(([requestData, currentChargerOverview]) => {
        return this.notesService
          .saveNewNote({
            ...requestData.note,
            chargerId: currentChargerOverview?.id,
          })
          .pipe(
            map((result) => {
              if (result.isSuccess) {
                return currentChargerActions.saveNewNoteSuccess({
                  note: result.value as NoteDto,
                });
              } else {
                return currentChargerActions.saveNewNoteFailure({
                  error: result.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                currentChargerActions.saveNewNoteFailure({
                  error: err.message || 'Save new note failed',
                })
              )
            )
          );
      })
    )
  );
}
